import React, { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Trash2,
  Upload,
  X
} from 'lucide-react';

interface ImportField {
  key: string;
  labelKey: string;
  defaultLabel: string;
}

const IMPORT_FIELDS: ImportField[] = [
  { key: 'firstName', labelKey: 'bulkImport.fields.firstName', defaultLabel: 'First Name' },
  { key: 'lastName', labelKey: 'bulkImport.fields.lastName', defaultLabel: 'Last Name' },
  { key: 'jobTitle', labelKey: 'bulkImport.fields.jobTitle', defaultLabel: 'Job Title' },
  { key: 'location', labelKey: 'bulkImport.fields.location', defaultLabel: 'Location (Display)' },
  { key: 'bio', labelKey: 'bulkImport.fields.bio', defaultLabel: 'Bio' },
  { key: 'email', labelKey: 'bulkImport.fields.email', defaultLabel: 'Email' },
  { key: 'cardId', labelKey: 'bulkImport.fields.cardId', defaultLabel: 'Card-ID' },
  { key: 'password', labelKey: 'bulkImport.fields.password', defaultLabel: 'Password' },
  { key: 'role', labelKey: 'bulkImport.fields.role', defaultLabel: 'Role' },
  { key: 'department', labelKey: 'bulkImport.fields.department', defaultLabel: 'Department' },
  { key: 'phone1', labelKey: 'bulkImport.fields.phone1', defaultLabel: 'Phone 1' },
  { key: 'phone2', labelKey: 'bulkImport.fields.phone2', defaultLabel: 'Phone 2' },
  { key: 'street', labelKey: 'bulkImport.fields.street', defaultLabel: 'Street' },
  { key: 'postalCode', labelKey: 'bulkImport.fields.postalCode', defaultLabel: 'Postal Code' },
  { key: 'city', labelKey: 'bulkImport.fields.city', defaultLabel: 'City' },
  { key: 'country', labelKey: 'bulkImport.fields.country', defaultLabel: 'Country' },
  { key: 'showRoute', labelKey: 'bulkImport.fields.showRoute', defaultLabel: 'Show route' },
  { key: 'website', labelKey: 'bulkImport.fields.website', defaultLabel: 'Website' },
  { key: 'linkedin', labelKey: 'bulkImport.fields.linkedin', defaultLabel: 'LinkedIn URL' },
  { key: 'twitter', labelKey: 'bulkImport.fields.twitter', defaultLabel: 'Twitter / X' },
  { key: 'instagram', labelKey: 'bulkImport.fields.instagram', defaultLabel: 'Instagram' },
  { key: 'github', labelKey: 'bulkImport.fields.github', defaultLabel: 'Github' }
];

const HEADER_ALIASES: Record<string, string[]> = {
  firstName: ['first name', 'firstname', 'vorname'],
  lastName: ['last name', 'lastname', 'surname', 'nachname'],
  jobTitle: ['job title', 'title', 'position', 'jobtitle', 'jobtitel'],
  location: ['location (display)', 'location', 'standort', 'ort', 'standort anzeige'],
  bio: ['bio', 'beschreibung', 'about'],
  email: ['email', 'e-mail', 'mail'],
  cardId: ['card-id', 'card id', 'cardid', 'card-id (slug)', 'card id slug', 'karten-id', 'kartenid', 'slug', 'card slug'],
  password: ['password', 'passwort', 'initial password', 'temporary password'],
  role: ['role', 'rolle', 'user role'],
  department: ['department', 'abteilung', 'team'],
  phone1: ['phone 1', 'phone1', 'telefon 1', 'telefon1', 'phone'],
  phone2: ['phone 2', 'phone2', 'telefon 2', 'telefon2'],
  street: ['street', 'strasse', 'straße'],
  postalCode: ['postal code', 'postalcode', 'zip', 'plz', 'postleitzahl'],
  city: ['city', 'stadt'],
  country: ['country', 'land'],
  showRoute: ['show route', 'show route button', 'route', 'showroute', 'route anzeigen'],
  website: ['website', 'webseite', 'url'],
  linkedin: ['linkedin url', 'linkedin', 'linkedin-url'],
  twitter: ['twitter / x', 'twitter', 'x', 'twitter/x'],
  instagram: ['instagram'],
  github: ['github', 'git hub']
};

const BOOLEAN_TRUE_VALUES = new Set(['true', '1', 'yes', 'y', 'ja', 'x', 'on']);
const BOOLEAN_FALSE_VALUES = new Set(['false', '0', 'no', 'n', 'nein', 'off']);
const ROLE_OPTIONS = ['member', 'department_lead', 'admin', 'owner'];
const URL_FIELDS = new Set(['website', 'linkedin', 'twitter', 'instagram', 'github']);

const normalizeHeader = (value: unknown = ''): string =>
  String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const normalizeCellValue = (value: unknown): string =>
  String(value ?? '').replace(/\uFEFF/g, '').trim();

const parseBooleanCell = (value: unknown): boolean | null => {
  const normalized = normalizeCellValue(value).toLowerCase();
  if (!normalized) return true;
  if (BOOLEAN_TRUE_VALUES.has(normalized)) return true;
  if (BOOLEAN_FALSE_VALUES.has(normalized)) return false;
  return null;
};

const isBlankRow = (row: Record<string, unknown>): boolean =>
  IMPORT_FIELDS.every((field) => !normalizeCellValue(row[field.key]));

const isValidUrl = (value: unknown): boolean => {
  const normalized = normalizeCellValue(value);
  if (!normalized) return true;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidEmail = (value: unknown): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeCellValue(value));

const normalizeRoleValue = (value: unknown): string =>
  normalizeCellValue(value).toLowerCase().replace(/\s+/g, '_');

interface ParsedRow {
  id: string;
  sourceRow: number;
  firstName: string;
  lastName: string;
  jobTitle: string;
  location: string;
  bio: string;
  email: string;
  cardId: string;
  password: string;
  role: string;
  department: string;
  phone1: string;
  phone2: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  showRoute: string;
  website: string;
  linkedin: string;
  twitter: string;
  instagram: string;
  github: string;
  [key: string]: unknown;
}

interface ValidationResult extends ParsedRow {
  email: string;
  role: string;
  normalizedShowRoute: boolean;
  errors: Record<string, string>;
  errorMessages: string[];
  isValid: boolean;
}

interface BulkImportResult {
  importedCount: number;
  skippedCount: number;
  errors: Array<{ row?: number; email?: string; error?: string; errors?: string[] }>;
}

interface OrgOption { id: string; name: string; }

interface BulkUserImportProps {
  isOpen: boolean;
  onClose?: () => void;
  apiCall: (url: string, options?: RequestInit) => Promise<Response>;
  organisationId?: string;
  organisationName?: string;
  /** When provided, renders an org selector inside the modal (super-admin use) */
  orgOptions?: OrgOption[];
  showAlert?: (message: string, type?: string) => void;
  onImported?: (payload: unknown) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFunction = (key: string, options?: any) => string;

const buildTemplateWorkbook = (t: TranslateFunction) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(
    t('bulkImport.template.sheetName', { defaultValue: 'Import Template' })
  );

  const headers: Record<string, string> = {};
  IMPORT_FIELDS.forEach((field) => {
    headers[field.key] = t(field.labelKey, { defaultValue: field.defaultLabel });
  });

  // Add header row
  worksheet.addRow(IMPORT_FIELDS.map((field) => headers[field.key]));

  // Sample row data as arrays matching IMPORT_FIELDS order
  const sampleData1: (string | undefined)[] = [
    'Jürgen', // firstName
    'Müller', // lastName
    'Sales Manager', // jobTitle
    'Berlin, Germany', // location
    'Short profile text for the imported card.', // bio
    'juergen.mueller@example.com', // email
    'juergen-mueller', // cardId
    'Welcome123!', // password
    'member', // role
    'Sales', // department
    '+49 30 123456', // phone1
    '', // phone2
    'Musterstraße 1', // street
    '10115', // postalCode
    'Berlin', // city
    'DE', // country
    'true', // showRoute
    'https://example.com', // website
    'https://linkedin.com/in/juergen-mueller', // linkedin
    '', // twitter
    '', // instagram
    '' // github
  ];

  const sampleData2: (string | undefined)[] = [
    'Anna', // firstName
    'Schneider', // lastName
    'Head of Consulting', // jobTitle
    'Hamburg, Germany', // location
    'Example row for a department lead import.', // bio
    'anna.schneider@example.com', // email
    '', // cardId (auto-generated from name when empty)
    'Consulting123!', // password
    'department_lead', // role
    'Consulting', // department
    '+49 40 987654', // phone1
    '', // phone2
    'Elbchaussee 5', // street
    '22765', // postalCode
    'Hamburg', // city
    'DE', // country
    'false', // showRoute
    'https://example.org', // website
    'https://linkedin.com/in/anna-schneider', // linkedin
    'https://x.com/anna_schneider', // twitter
    '', // instagram
    '' // github
  ];

  worksheet.addRow(sampleData1);
  worksheet.addRow(sampleData2);

  // Set column widths
  const colWidths: Partial<ExcelJS.Column>[] = IMPORT_FIELDS.map((field) => ({
    width: headers[field.key]!.length + 2
  }));
  worksheet.columns = colWidths as ExcelJS.Column[];

  return workbook;
};

const escapeCsvCell = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const buildTemplateCsv = (t: TranslateFunction): string => {
  const headers = IMPORT_FIELDS.map((field) => t(field.labelKey, { defaultValue: field.defaultLabel }));

  const sampleData1 = [
    'Jürgen', 'Müller', 'Sales Manager', 'Berlin, Germany',
    'Short profile text for the imported card.', 'juergen.mueller@example.com',
    'juergen-mueller', 'Welcome123!', 'member', 'Sales',
    '+49 30 123456', '', 'Musterstraße 1', '10115', 'Berlin', 'DE',
    'true', 'https://example.com', 'https://linkedin.com/in/juergen-mueller', '', '', ''
  ];
  const sampleData2 = [
    'Anna', 'Schneider', 'Head of Consulting', 'Hamburg, Germany',
    'Example row for a department lead import.', 'anna.schneider@example.com',
    '', 'Consulting123!', 'department_lead', 'Consulting',
    '+49 40 987654', '', 'Elbchaussee 5', '22765', 'Hamburg', 'DE',
    'false', 'https://example.org', 'https://linkedin.com/in/anna-schneider', 'https://x.com/anna_schneider', '', ''
  ];

  const csvRows = [headers, sampleData1, sampleData2]
    .map((row) => row.map((cell) => escapeCsvCell(String(cell ?? ''))).join(','))
    .join('\r\n');

  return '\uFEFF' + csvRows; // UTF-8 BOM for Excel compatibility
};

const decodeCsvText = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);
  const hasUtf8Bom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  const candidates = hasUtf8Bom ? ['utf-8'] : ['utf-8', 'windows-1252', 'iso-8859-1'];

  let best = '';
  let bestScore = Number.POSITIVE_INFINITY;

  for (const encoding of candidates) {
    try {
      const decoded = new TextDecoder(encoding, { fatal: false }).decode(bytes);
      const replacementCount = (decoded.match(/\uFFFD/g) || []).length;
      const delimiterScore = (decoded.match(/[;,]/g) || []).length > 0 ? 0 : 10;
      const newlineScore = decoded.includes('\n') ? 0 : 10;
      const score = replacementCount * 100 + delimiterScore + newlineScore;
      if (score < bestScore) {
        best = decoded;
        bestScore = score;
      }
      if (score === 0) break;
    } catch {
      // Ignore and try the next decoder.
    }
  }

  return best.replace(/^\uFEFF/, '');
};

const buildHeaderMap = (headerRow: string[]): Record<string, number> => {
  const normalizedHeaders = headerRow.map((cell) => normalizeHeader(cell));
  const headerMap: Record<string, number> = {};

  IMPORT_FIELDS.forEach((field) => {
    // Build alias set: explicit aliases + the field's own defaultLabel.
    // All are normalized the same way as actual headers so e.g.
    // "Card-ID (Slug)" (→ "card id slug") matches alias "card-id (slug)" (→ "card id slug").
    const aliases = new Set([
      ...(HEADER_ALIASES[field.key] || []).map(normalizeHeader),
      normalizeHeader(field.defaultLabel),
    ]);
    const index = normalizedHeaders.findIndex((header) => aliases.has(header));
    if (index !== -1) {
      headerMap[field.key] = index;
    }
  });

  return headerMap;
};

// ExcelJS can return hyperlink objects { text, hyperlink } or rich-text objects
// { richText: [{text},...] } instead of plain strings — normalise them here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const excelCellToString = (cellValue: any): string => {
  if (cellValue == null) return '';
  if (typeof cellValue === 'object') {
    if ('richText' in cellValue && Array.isArray(cellValue.richText)) {
      return (cellValue.richText as Array<{ text?: string }>).map((r) => r.text ?? '').join('');
    }
    if ('text' in cellValue) return String(cellValue.text ?? '');
    if ('result' in cellValue) return String(cellValue.result ?? ''); // formula result
  }
  return String(cellValue);
};

const parseWorkbookRows = async (file: File): Promise<ParsedRow[]> => {
  const fileName = file?.name || '';
  const isCsv = fileName.toLowerCase().endsWith('.csv');
  const arrayBuffer = await file.arrayBuffer();

  let headerRow: string[] = [];
  let dataRows: string[][] = [];

  if (isCsv) {
    const csvText = decodeCsvText(arrayBuffer);
    const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length > 0) {
      const delimiter = csvText.includes(';') ? ';' : ',';
      headerRow = lines[0]!.split(delimiter).map((cell) => cell.trim().replace(/^["']|["']$/g, ''));
      dataRows = lines.slice(1).map((line) => {
        const cells: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"' && !inQuotes) {
            inQuotes = true;
          } else if (char === '"' && inQuotes) {
            if (line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else if (char === delimiter && !inQuotes) {
            cells.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        cells.push(current.trim());
        return cells;
      });
    }
  } else {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    const worksheet = workbook.getWorksheet(1);
    if (!worksheet) return [];

    const rows: string[][] = [];
    worksheet.eachRow((row) => {
      const cells: string[] = [];
      for (let i = 1; i <= IMPORT_FIELDS.length; i++) {
        cells.push(excelCellToString(row.getCell(i).value));
      }
      rows.push(cells);
    });

    if (rows.length > 0) {
      headerRow = rows[0]!;
      dataRows = rows.slice(1);
    }
  }

  if (!headerRow.length) {
    return [];
  }

  const headerMap = buildHeaderMap(headerRow);

  return dataRows
    .map((cells, index) => {
      const mappedRow: Record<string, unknown> = {};
      IMPORT_FIELDS.forEach((field) => {
        const cellIndex = headerMap[field.key];
        mappedRow[field.key] = cellIndex !== undefined && cells[cellIndex] !== undefined
          ? normalizeCellValue(cells[cellIndex])
          : '';
      });

      return {
        id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        sourceRow: index + 2,
        ...mappedRow
      } as ParsedRow;
    })
    .filter((row) => !isBlankRow(row as unknown as Record<string, unknown>));
};

const DEFAULT_FALSE_ERRORS: Record<string, string> = Object.fromEntries(
  IMPORT_FIELDS.map((field) => [field.key, ''])
);

const validateImportRows = (rows: ParsedRow[], t: TranslateFunction): ValidationResult[] => {
  const seenEmails = new Map<string, number>();

  rows.forEach((row) => {
    const email = normalizeCellValue(row.email).toLowerCase();
    if (!email) return;
    seenEmails.set(email, (seenEmails.get(email) || 0) + 1);
  });

  return rows.map((row) => {
    const errors = { ...DEFAULT_FALSE_ERRORS };
    const email = normalizeCellValue(row.email).toLowerCase();
    const password = normalizeCellValue(row.password);
    const showRoute = parseBooleanCell(row.showRoute);
    const role = normalizeRoleValue(row.role || 'member');

    if (!email) {
      errors.email = t('bulkImport.validation.emailRequired', { defaultValue: 'Email is required' });
    } else if (!isValidEmail(email)) {
      errors.email = t('bulkImport.validation.emailInvalid', { defaultValue: 'Invalid email address' });
    } else if ((seenEmails.get(email) || 0) > 1) {
      errors.email = t('bulkImport.validation.emailDuplicate', { defaultValue: 'Duplicate email in import file' });
    }

    const cardId = normalizeCellValue(row.cardId).toLowerCase();
    if (cardId && !/^[a-z0-9][a-z0-9-]{0,78}[a-z0-9]$|^[a-z0-9]$/.test(cardId)) {
      errors.cardId = t('bulkImport.validation.cardIdInvalid', {
        defaultValue: 'Card-ID: only lowercase letters, numbers and hyphens (max 80 chars)'
      });
    }

    if (password && password.length < 8) {
      errors.password = t('bulkImport.validation.passwordTooShort', {
        defaultValue: 'Password must be at least 8 characters long'
      });
    }

    if (row.showRoute && showRoute === null) {
      errors.showRoute = t('bulkImport.validation.booleanInvalid', { defaultValue: 'Use true/false, yes/no, 1/0' });
    }

    if (row.role && !ROLE_OPTIONS.includes(role)) {
      errors.role = t('bulkImport.validation.roleInvalid', { defaultValue: 'Invalid role' });
    }

    if (role === 'department_lead' && !normalizeCellValue(row.department)) {
      errors.department = t('bulkImport.validation.departmentRequiredForLead', {
        defaultValue: 'Department is required for department leads'
      });
    }

    URL_FIELDS.forEach((field) => {
      if (row[field] && !isValidUrl(row[field])) {
        errors[field] = t('bulkImport.validation.urlInvalid', { defaultValue: 'Invalid URL' });
      }
    });

    const errorMessages = Object.values(errors).filter(Boolean);

    return {
      ...row,
      email,
      role,
      normalizedShowRoute: showRoute === null ? true : showRoute,
      errors,
      errorMessages,
      isValid: errorMessages.length === 0
    };
  });
};

export const mapImportRowToCardPayload = (row: ParsedRow, organisationName = '') => ({
  personal: {
    firstName: normalizeCellValue(row.firstName),
    lastName: normalizeCellValue(row.lastName),
    title: normalizeCellValue(row.jobTitle),
    company: organisationName,
    bio: normalizeCellValue(row.bio),
    location: normalizeCellValue(row.location)
  },
  contact: {
    email: normalizeCellValue(row.email).toLowerCase(),
    phone: normalizeCellValue(row.phone1),
    phoneLabel: '',
    phoneSecondary: normalizeCellValue(row.phone2),
    phoneSecondaryLabel: '',
    address: [
      normalizeCellValue(row.street),
      [normalizeCellValue(row.postalCode), normalizeCellValue(row.city)].filter(Boolean).join(' ').trim(),
      normalizeCellValue(row.country)
    ].filter(Boolean).join(', '),
    addressStreet: normalizeCellValue(row.street),
    addressPostalCode: normalizeCellValue(row.postalCode),
    addressCity: normalizeCellValue(row.city),
    addressCountry: normalizeCellValue(row.country),
    showRouteButton: parseBooleanCell(row.showRoute) === null ? true : parseBooleanCell(row.showRoute),
    website: normalizeCellValue(row.website)
  },
  social: {
    linkedin: normalizeCellValue(row.linkedin),
    twitter: normalizeCellValue(row.twitter),
    instagram: normalizeCellValue(row.instagram),
    github: normalizeCellValue(row.github)
  }
});

const emptyServerResult: BulkImportResult = {
  importedCount: 0,
  skippedCount: 0,
  errors: []
};

export default function BulkUserImport({
  isOpen,
  onClose,
  apiCall,
  organisationId,
  organisationName,
  orgOptions,
  showAlert,
  onImported
}: BulkUserImportProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [serverResult, setServerResult] = useState<BulkImportResult>(emptyServerResult);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  // Org selection — only used when orgOptions is provided (super-admin mode)
  const [localOrgId, setLocalOrgId] = useState(organisationId ?? '');
  const [localOrgName, setLocalOrgName] = useState(organisationName ?? '');
  React.useEffect(() => {
    setLocalOrgId(organisationId ?? '');
    setLocalOrgName(organisationName ?? '');
  }, [organisationId, organisationName]);

  const resolvedOrgId   = orgOptions ? localOrgId   : (organisationId ?? '');
  const resolvedOrgName = orgOptions ? localOrgName  : (organisationName ?? '');

  const validatedRows = useMemo(() => validateImportRows(rows, t), [rows, t]);
  const hasErrors = validatedRows.some((row) => !row.isValid);
  const totalErrorCount = validatedRows.reduce((sum, row) => sum + row.errorMessages.length, 0);

  if (!isOpen) return null;

  const resetState = () => {
    setRows([]);
    setFileName('');
    setIsParsing(false);
    setIsImporting(false);
    setServerResult(emptyServerResult);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const updateCell = (rowId: string, field: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsParsing(true);
    setServerResult(emptyServerResult);
    try {
      const parsedRows = await parseWorkbookRows(file);
      setRows(parsedRows);
      setFileName(file.name);

      if (parsedRows.length === 0 && showAlert) {
        showAlert(
          t('bulkImport.alerts.noRows', { defaultValue: 'No importable rows were found in the file.' }),
          'info'
        );
      }
    } catch (error) {
      console.error('Failed to parse bulk import file:', error);
      if (showAlert) {
        showAlert(
          t('bulkImport.alerts.parseFailed', { defaultValue: 'The file could not be parsed.' }),
          'error'
        );
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!resolvedOrgId) {
      showAlert?.(
        t('bulkImport.alerts.organisationRequired', { defaultValue: 'Please select an organisation first.' }),
        'error'
      );
      return;
    }

    if (validatedRows.length === 0) {
      showAlert?.(
        t('bulkImport.alerts.noRows', { defaultValue: 'No importable rows were found in the file.' }),
        'info'
      );
      return;
    }

    if (hasErrors) {
      showAlert?.(
        t('bulkImport.alerts.fixErrors', { defaultValue: 'Please fix the highlighted rows before importing.' }),
        'error'
      );
      return;
    }

    setIsImporting(true);
    try {
      const response = await apiCall('/api/admin/users/bulk-import', {
        method: 'POST',
        body: JSON.stringify({
          organisationId: resolvedOrgId,
          rows: validatedRows.map(({ errors, errorMessages, isValid, normalizedShowRoute, id, sourceRow, ...row }) => ({
            ...row,
            role: row.role || 'member',
            showRoute: normalizedShowRoute,
            cardId: normalizeCellValue(row.cardId).toLowerCase() || undefined
          }))
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        showAlert?.(
          (payload as { error?: string }).error || t('bulkImport.alerts.importFailed', { defaultValue: 'Bulk import failed.' }),
          'error'
        );
        return;
      }

      const result: BulkImportResult = {
        importedCount: (payload as { importedCount?: number }).importedCount || 0,
        skippedCount: (payload as { skippedCount?: number }).skippedCount || 0,
        errors: Array.isArray((payload as { errors?: unknown }).errors)
          ? ((payload as { errors: unknown[] }).errors as BulkImportResult['errors'])
          : []
      };
      setServerResult(result);

      if (result.importedCount > 0) {
        showAlert?.(
          t('bulkImport.alerts.importSuccess', {
            defaultValue: '{{count}} users were imported successfully.',
            count: result.importedCount
          }),
          'success'
        );
      }

      if (result.skippedCount > 0 && showAlert) {
        showAlert(
          t('bulkImport.alerts.importPartial', {
            defaultValue: '{{count}} rows were skipped. Review the import result below.',
            count: result.skippedCount
          }),
          'warning'
        );
      }

      onImported?.(payload);

      if (result.skippedCount === 0) {
        handleClose();
      }
    } catch (error) {
      console.error('Bulk import failed:', error);
      showAlert?.(
        t('bulkImport.alerts.importError', { defaultValue: 'An error occurred while importing the users.' }),
        'error'
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    setShowTemplateMenu(false);
    const workbook = buildTemplateWorkbook(t);
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('bulkImport.template.fileName', { defaultValue: 'vcard-bulk-import-template.xlsx' });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  const handleDownloadCsvTemplate = () => {
    setShowTemplateMenu(false);
    const csv = buildTemplateCsv(t);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = t('bulkImport.template.fileNameCsv', { defaultValue: 'vcard-bulk-import-template.csv' });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Shared input class for the data table — works in both light and dark contexts
  const cellInput = (hasError: boolean) =>
    `w-full px-2.5 py-1.5 rounded-lg border text-sm outline-none transition focus:ring-2 focus:ring-accent-500/40 bg-raised ${
      hasError
        ? 'border-danger-500/60 text-danger-700 dark:text-danger-300 placeholder:text-danger-400/50'
        : 'border-line dark:border-white/10 text-white dark:text-white placeholder:text-ink-faint'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — always dark regardless of parent theme */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal — dark-first design */}
      <div className="relative w-full max-w-[96vw] xl:max-w-[1480px] max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-line bg-panel">

        {/* ── Header ── */}
        <div className="px-6 py-5 border-b border-line flex items-start justify-between gap-6 bg-panel">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-accent-600/20 border border-accent-500/30 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-accent-700 dark:text-accent-300" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-ink leading-tight">
                {t('bulkImport.title', { defaultValue: 'Bulk user import' })}
              </h2>
              {/* Org selector (super-admin) or static org name */}
              {orgOptions && orgOptions.length > 0 ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-ink-muted shrink-0">
                    {t('common.fields.organisation', { defaultValue: 'Organisation' })}:
                  </span>
                  <select
                    value={localOrgId}
                    onChange={(e) => {
                      const id = e.target.value;
                      const name = orgOptions.find((o) => o.id === id)?.name ?? '';
                      setLocalOrgId(id);
                      setLocalOrgName(name);
                    }}
                    className="rounded-lg bg-panel border border-line px-2 py-0.5 text-sm text-ink focus:outline-none focus:border-accent-500/50 transition"
                  >
                    {orgOptions.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
              ) : resolvedOrgName ? (
                <p className="text-sm text-ink-muted mt-0.5">
                  {t('bulkImport.subtitle', {
                    defaultValue: 'Import into {{organisation}}',
                    organisation: resolvedOrgName
                  })}
                </p>
              ) : null}
              <p className="text-xs text-ink-faint mt-1">
                {t('bulkImport.helper', {
                  defaultValue: 'Supported: .xlsx and .csv — edit inline before importing.'
                })}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="shrink-0 p-2 rounded-lg bg-panel border border-line text-ink-muted hover:bg-raised hover:text-white transition"
            aria-label={t('common.actions.close', { defaultValue: 'Close' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="px-6 py-3 border-b border-line flex flex-wrap items-center gap-3 bg-panel">
          {/* File upload */}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition shadow-sm">
            {isParsing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span>{t('bulkImport.actions.selectFile', { defaultValue: 'Select file' })}</span>
            <input type="file" accept=".xlsx,.csv" onChange={handleFileChange} className="hidden" />
          </label>

          {/* Template dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTemplateMenu((v) => !v)}
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-line bg-panel text-ink text-sm font-medium hover:bg-raised hover:text-white transition"
            >
              <Download className="w-4 h-4" />
              <span>{t('bulkImport.actions.downloadTemplate', { defaultValue: 'Template' })}</span>
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </button>
            {showTemplateMenu && (
              <div className="absolute top-full left-0 mt-1 z-50 min-w-[160px] rounded-xl border border-line bg-raised shadow-xl overflow-hidden">
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-raised hover:text-white flex items-center gap-2 transition"
                >
                  <FileSpreadsheet className="w-4 h-4 shrink-0 text-success-400" />
                  <span>Excel (.xlsx)</span>
                </button>
                <button
                  onClick={handleDownloadCsvTemplate}
                  className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-raised hover:text-white flex items-center gap-2 transition"
                >
                  <FileText className="w-4 h-4 shrink-0 text-accent-400" />
                  <span>CSV (.csv)</span>
                </button>
              </div>
            )}
          </div>

          {/* Active file chip */}
          {fileName && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-panel border border-line text-sm text-ink-muted font-mono truncate max-w-[240px]">
              <FileSpreadsheet className="w-3.5 h-3.5 shrink-0 text-accent-400" />
              {fileName}
            </span>
          )}

          {/* Spacer + counters */}
          <div className="ml-auto flex items-center gap-2 text-xs font-medium">
            <span className="px-3 py-1.5 rounded-lg bg-panel border border-line text-ink-muted">
              {t('bulkImport.summary.rows', { defaultValue: 'Rows: {{count}}', count: validatedRows.length })}
            </span>
            <span className={`px-3 py-1.5 rounded-lg border ${
              totalErrorCount > 0
                ? 'bg-danger-100 dark:bg-danger-500/10 border-danger-500/30 text-danger-700 dark:text-danger-300'
                : 'bg-panel border-line text-ink-muted'
            }`}>
              {t('bulkImport.summary.errors', { defaultValue: 'Errors: {{count}}', count: totalErrorCount })}
            </span>
          </div>
        </div>

        {/* ── Table / Empty state ── */}
        <div className="flex-1 overflow-auto">
          {validatedRows.length === 0 ? (
            <div className="h-full min-h-[320px] flex flex-col items-center justify-center p-10 text-center">
              <div className="w-16 h-16 rounded-2xl bg-panel border border-line flex items-center justify-center mb-5">
                <FileSpreadsheet className="w-7 h-7 text-ink-faint" />
              </div>
              <h3 className="text-base font-semibold text-ink mb-2">
                {t('bulkImport.empty.title', { defaultValue: 'No file loaded yet' })}
              </h3>
              <p className="text-sm text-ink-muted max-w-md">
                {t('bulkImport.empty.description', {
                  defaultValue:
                    'Upload a spreadsheet to preview every row, validate the imported values, and correct data inline before writing users and cards into the database.'
                })}
              </p>
            </div>
          ) : (
            <div className="min-w-max">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-panel border-b border-line">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-ink-muted font-semibold uppercase tracking-wide whitespace-nowrap">
                      {t('bulkImport.table.row', { defaultValue: '#' })}
                    </th>
                    {IMPORT_FIELDS.map((field) => (
                      <th key={field.key} className="px-3 py-2.5 text-left text-ink-muted font-semibold uppercase tracking-wide whitespace-nowrap min-w-[160px]">
                        {t(field.labelKey, { defaultValue: field.defaultLabel })}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-left text-ink-muted font-semibold uppercase tracking-wide whitespace-nowrap min-w-[200px]">
                      {t('bulkImport.table.validation', { defaultValue: 'Status' })}
                    </th>
                    <th className="px-3 py-2.5 text-left text-ink-muted font-semibold uppercase tracking-wide whitespace-nowrap">
                      {t('common.fields.actions', { defaultValue: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {validatedRows.map((row) => (
                    <tr key={row.id} className={`align-top transition-colors ${row.isValid ? 'hover:bg-white/[0.02]' : 'bg-danger-500/[0.04] hover:bg-danger-500/[0.07]'}`}>
                      <td className="px-3 py-2.5 text-ink-faint font-mono">{row.sourceRow}</td>
                      {IMPORT_FIELDS.map((field) => {
                        const hasError = Boolean(row.errors[field.key]);

                        if (field.key === 'bio') {
                          return (
                            <td key={field.key} className="px-3 py-2.5">
                              <textarea rows={2} value={String(row[field.key] ?? '')} onChange={(e) => updateCell(row.id, field.key, e.target.value)} className={cellInput(hasError)} />
                              {hasError && <div className="mt-1 text-danger-400">{row.errors[field.key]}</div>}
                            </td>
                          );
                        }
                        if (field.key === 'showRoute') {
                          return (
                            <td key={field.key} className="px-3 py-2.5">
                              <select value={String(row[field.key] ?? '')} onChange={(e) => updateCell(row.id, field.key, e.target.value)} className={cellInput(hasError)}>
                                <option value="">{t('bulkImport.boolean.default', { defaultValue: 'Default: on' })}</option>
                                <option value="true">{t('common.states.enabled', { defaultValue: 'Yes' })}</option>
                                <option value="false">{t('common.states.disabled', { defaultValue: 'No' })}</option>
                              </select>
                              {hasError && <div className="mt-1 text-danger-400">{row.errors[field.key]}</div>}
                            </td>
                          );
                        }
                        if (field.key === 'role') {
                          return (
                            <td key={field.key} className="px-3 py-2.5">
                              <select value={String(row[field.key] ?? 'member')} onChange={(e) => updateCell(row.id, field.key, e.target.value)} className={cellInput(hasError)}>
                                <option value="member">{t('common.roles.member', { defaultValue: 'Member' })}</option>
                                <option value="department_lead">{t('common.roles.departmentLead', { defaultValue: 'Dept. Lead' })}</option>
                                <option value="admin">{t('common.roles.admin', { defaultValue: 'Verwalter' })}</option>
                                <option value="owner">{t('common.roles.owner', { defaultValue: 'Owner' })}</option>
                              </select>
                              {hasError && <div className="mt-1 text-danger-400">{row.errors[field.key]}</div>}
                            </td>
                          );
                        }
                        return (
                          <td key={field.key} className="px-3 py-2.5">
                            <input
                              type={URL_FIELDS.has(field.key) ? 'url' : field.key === 'email' ? 'email' : 'text'}
                              value={String(row[field.key] ?? '')}
                              onChange={(e) => updateCell(row.id, field.key, e.target.value)}
                              className={cellInput(hasError)}
                            />
                            {hasError && <div className="mt-1 text-danger-400">{row.errors[field.key]}</div>}
                          </td>
                        );
                      })}
                      {/* Status */}
                      <td className="px-3 py-2.5">
                        {row.isValid ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-100 dark:bg-success-500/10 border border-success-500/20 text-success-700 dark:text-success-300 text-xs font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t('bulkImport.validation.rowValid', { defaultValue: 'Ready' })}
                          </span>
                        ) : (
                          <div className="space-y-1">
                            {row.errorMessages.map((message, index) => (
                              <div key={`${row.id}-err-${index}`} className="flex items-start gap-1.5 text-danger-700 dark:text-danger-300">
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span>{message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      {/* Remove */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => removeRow(row.id)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-danger-500/20 bg-danger-100 dark:bg-danger-500/10 text-danger-700 dark:text-danger-300 hover:bg-danger-500/20 transition text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Server errors ── */}
        {serverResult.errors.length > 0 && (
          <div className="px-6 py-3 border-t border-line bg-danger-500/5 max-h-40 overflow-auto">
            <p className="text-xs font-semibold text-danger-700 dark:text-danger-300 mb-2">
              {t('bulkImport.result.title', { defaultValue: 'Import result' })}
            </p>
            <div className="space-y-1">
              {serverResult.errors.map((error, index) => (
                <div key={`bulk-result-${index}`} className="text-xs text-danger-300/80">
                  {t('bulkImport.result.rowError', {
                    defaultValue: 'Row {{row}} ({{email}}): {{message}}',
                    row: error.row || '?',
                    email: error.email || t('common.fields.unknown', { defaultValue: 'Unknown' }),
                    message: Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || '')
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-line bg-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-ink-faint">
            {t('bulkImport.footer.note', {
              defaultValue:
                'Rows without a password get an auto-generated temporary password. Existing users are skipped.'
            })}
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-xl border border-line bg-panel text-ink text-sm font-medium hover:bg-raised hover:text-white transition"
            >
              {t('common.actions.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || isParsing || validatedRows.length === 0 || hasErrors}
              className="px-5 py-2 rounded-xl bg-accent-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition inline-flex items-center gap-2 shadow-sm"
            >
              {isImporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span>
                {isImporting
                  ? t('bulkImport.actions.importing', { defaultValue: 'Importing…' })
                  : t('bulkImport.actions.import', { defaultValue: 'Import users' })}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
